import {ReportWebTemplateRes} from '@/axios/geo';
import {IListInfo} from './common';

/** geo任务的单项的信息 */
export interface IGEOTaskInfo {
  id: string;
  /** 名称 */
  taskName: string;
  /** 描述 */
  companyName: string;
  keywordsConfig: {
    /** 核心词列表 */
    coreKeywords?: string[];
    /** 蒸馏词列表 */
    distillationKeywords?: string[];
  };
  createTime: string;
  updateTime: string;
}

/** 列表的整体信息 */
export type TGEOTaskListInfo = IListInfo<IGEOTaskInfo>;

export const DEFAULT_GEO_TASK_LIST_INFO: TGEOTaskListInfo = {
  pageIndex: 1,
  pageSize: 20,
  total: 0,
  isLoading: false,
  isHasFetch: false,
  list: [],
};

export enum ERunStatus {
  Waiting = 'WAITING',
  Running = 'RUNNING',
  Success = 'SUCC',
  Fail = 'FAILED',
  TIMEOUT = 'TIMEOUT',
  STOPPED = 'STOPPED',
}

export enum EPublishStatus {
  UNPUBLISHED = 'UNPUBLISHED',
  PUBLISHED = 'PUBLISHED',
}

export type LegacyPublishStatus = 'UNPUBLISH' | 'PUBLISH';
export type CommonPublishStatus = EPublishStatus | LegacyPublishStatus | 0 | 1;

export const isPublishedStatus = (publishStatus?: CommonPublishStatus | null) => {
  return publishStatus === EPublishStatus.PUBLISHED || publishStatus === 'PUBLISH' || publishStatus === 1;
};

export const getPublishStatusText = (publishStatus?: CommonPublishStatus | null) => {
  return isPublishedStatus(publishStatus) ? '已发布' : '未发布';
};

export const RUN_STATUS_MAP: Record<ERunStatus, string> = {
  [ERunStatus.Waiting]: '等待中',
  [ERunStatus.Running]: '运行中',
  [ERunStatus.Success]: '成功',
  [ERunStatus.Fail]: '失败',
  [ERunStatus.STOPPED]: '中断',
  [ERunStatus.TIMEOUT]: '超时',
};
export const runStatusOptions = Object.entries(RUN_STATUS_MAP).map(([value, label]) => ({
  value,
  label,
}));

export interface ILinkInfo {
  /** 网站名称 */
  siteName: string;
  title: string;
  url: string;
  iconUrl: string;
  content: string;
}

/** 倾向性 */
export enum ESentiment {
  Positive = 'positive',
  Negative = 'negative',
  Middle = 'middle',
}

export const SENTIMENT_CONFIG: Record<
  ESentiment,
  {
    name: string;
    color: string;
  }
> = {
  [ESentiment.Positive]: {
    name: '积极',
    // 橙色
    color: '#FA8846',
  },
  [ESentiment.Negative]: {
    name: '消极',
    // 黑色
    color: '#000000',
  },
  [ESentiment.Middle]: {
    name: '中立',
    // 蓝色
    color: '#5D8AF2',
  },
};

/** 单次任务结果 */
export interface ISingleTaskResult {
  errorMsg?: string | null;
  shareUrl?: string;
  wordId: string;
  question: string;
  answer: string;
  /** 截图链接 */
  screenSnapshotUrls: string;
  /** 链接信息列表 */
  linkInfoList: ILinkInfo[];
  status: ERunStatus;
  aiResult?: {
    /** 可见性 */
    companyNameExist: boolean;
    /** 倾向性 */
    sentiment: ESentiment;
    /** 竞品品牌 */
    competitorNames?: string[];
    /** 企业排名 */
    companyRanking?: number;
    /** 准确率 */
    accuracy?: number;
  };
}

/** 列表中运行记录的信息 */
export interface IRunRecordInfo {
  /** 唯一 id */
  id: string;
  startTime: string;
  finishTime: string;
  /** 运行状态 */
  runStatus: ERunStatus;
  /** 任务总数量 */
  total: number;
  /** 任务失败数量 */
  fail: number;
  /** 任务正常完成数量 */
  complete: number;
  /** 创建时间 */
  createTime: string;
  platformMappings: { platform: string; name: string }[];
  taskName: string;
  /** 搜索词组名称 */
  phraseGroupName: string;
  /** 企业ID 历史原因原来的taskId是作为一个企业 */
  taskId: string;
  /** 企业名称 */
  companyName: string;
  /** 搜索词组 */
  componentName: string[];
  /** 曝光度 */
  exposure?: number;
  publishStatus?: CommonPublishStatus;
  publishStatusText?: string;

  expandWords: { recordId: string; recordName: string }[];
}

/** 列表的整体信息 */
export type TRunRecordListInfo = IListInfo<IRunRecordInfo>;

export const DEFAULT_RUN_RECORD_LIST: TRunRecordListInfo = {
  pageIndex: 1,
  pageSize: 20,
  total: 0,
  isLoading: false,
  isHasFetch: false,
  list: [],
};

interface IAIVisibility {
  /** 可见数 */
  visible: number;
  /** 总数 */
  total: number;
  /** 扩展可见数 */
  expand: number;
  totalVisible: number;
}

export interface IAIInclination {
  /** 积极数 */
  positiveTotal: number;
  positivePercent: number;
  /** 中立数 */
  neutralTotal: number;
  neutralPercent: number;
  /** 消极数 */
  negativeTotal: number;
  negativePercent: number;
}

/** 倾向性平台keys */
export const INCLINATION_KEYS = ['positive', 'neutral', 'negative'] as const;

/** 倾向性标签 */
export const INCLINATION_LABELS = ['积极', '中立', '消极'];

/** 引用源 keys */
export const REFERENCE_SOURCE_KEYS = ['brand', 'competitors'];

/** 饼图颜色(柱状图也用) */
export const DONUT_COLORS = ['#FA8846', '#FACE4B', '#79C7FF', '#B973FF', '#5D8AF2'];

export interface IReferenceSource {
  /** 本品牌 */
  brand: number;
  /** 竞品 */
  competitors: number;
  /** 其他 */
  others: number;
}

/** 监控任务详情 */
export interface TaskStatisticsVo {
  analysisResult: {
    /** AI可见性 */
    aiVisibility: IAIVisibility;
    /** AI可见性平台数据 */
    aiVisibilityPlatformData: Record<string, IAIVisibility>;
    /** AI可见性平台百分比数据 */
    aiVisibilityPlatformPercentData: Record<string, {
      percent: number;
      visible: number;
    }>;
    /** AI倾向性 */
    aiInclination: IAIInclination;
    /** AI倾向性平台数据 */
    aiInclinationPlatformData: Record<string, IAIInclination>;
    /** 覆盖搜索词平台数据 */
    coverSearchKeywordsPlatformData: Record<string, string[]>;
    competitor: {
      brand: number;
      items: { name: string; count: number }[];
      total: number;
    };
    competitorPlatformData: {
      [key in string]: {
        brand: number;
        items: { name: string; count: number }[];
        total: number;
      };
    };
  };
  [key: string]: any;
}
/** 报表详情 */
export interface IReportDetail {
  /** AI可见性 */
  aiVisibility: {
    visiblePercent: number;
    totalVisible: number;
  };
  /** AI可见性平台数据 */
  aiVisibilityPlatformData: {
    [key in string]: {
      totalVisible: number;
      visiblePercent: number;
    };
  };
  aiVisibilityPlatformPercentData: {
    [key in string]: {
      percent: number;
      visible: number;
    };
  };
  /** AI倾向性 */
  aiInclination: IAIInclination;
  /** AI倾向性平台数据 */
  aiInclinationPlatformData: {
    [key in string]: IAIInclination;
  };
  /** 覆盖搜索词平台数据 */
  coverSearchKeywordsPlatformData: Record<string, string[]>;
  competitor: {
    brand: number;
    items: { name: string; count: number }[];
    total: number;
  };
  competitorPlatformData: {
    [key in string]: {
      brand: number;
      items: { name: string; count: number }[];
      total: number;
    };
  };
  platformMappings: { platform: string; name: string }[];
  createTime: string;
  updateTime: string;
  keywordsConfig: {
    /** 核心词列表 */
    coreKeywords?: string[];
    /** 蒸馏词列表 */
    distillationKeywords?: string[];
  };
  companyName: string;
  officialWebsite: string;
  reportId: string;
  brandTotal?: number;
  searchTotal?: number;
  brandVisibility?: number;
  searchVisibility?: number;
  /** 平均排名 */
  avgRanking?: number;
  /** 引用信源统计 */
  referenceSourceStatistics?: {
    count: number;
    sourceName: string;
    ratio?: number | null;
  }[];
  /** 正面品牌词云 */
  positiveWordCloud?: unknown;
}

export interface IChartVisibilityValue {
  /** 乘过可行性百分比的值 */
  value: number;
  /** 真实值占得百分比 */
  realValue: number;
}

/** 柱状图堆叠数据 */
export interface IChartStackedBarValue {
  /** 乘过可行性百分比的值 */
  value: number;
  /** 真实值占得百分比 */
  realValue: number;
  /** 中文名称 */
  label: string;
}

export interface IOverrideSearchKeywordsNav {
  /** 平台 */
  platform: string;
  /** 搜索词列表数量 */
  listCount: number;
}

/** 报表页面数据 */
export interface IReportPageData {
  /** 页面名称(固定文案) */
  name: string;
  /** 日期信息 */
  dateInfo: {
    year: number;
    dayOfWeek: string;
    date: string;
  };
  /** 大模型平台列表 */
  aiPlatformList: string[];
  /** 大模型平台列表中文 */
  aiPlatformNameList: string[];
  /** 是否平台较少 */
  isFewPlatforms: boolean;
  /** 核心词列表 */
  coreKeywords: string[];
  /** 整体的 ai 可见性数(0-100) */
  aiVisibilityNumber: number;
  /** AI倾向性的数(0-100) */
  aiInclinationNumber: IAIInclination;
  /** AI可见性平台数字数据 */
  aiVisibilityPlatformNumberData: IChartVisibilityValue[];
  aiInclinationPlatformChartData: IChartStackedBarValue[][];
  /** OEM配置 */
  oemConfig: ReportWebTemplateRes;
  /** AI 品牌竞争力 环形图数据 */
  brandCompetitivenessData: {
    name: string;
    value: number;
    percentage: number;
  }[];
  /** AI 品牌竞争力 堆叠数据 */
  brandCompetitivenessPlatformChartData: IChartStackedBarValue[][];
  platformMappings: { platform: string; name: string }[];
  platformVisibleTableData: {
    /** 平台 */
    platform: string;
    /** 占比 */
    percentage: number;
    /** 已收录搜索词 */
    searchWords: number;
  }[];
  reportDetail: IReportDetail;
}
